import { NgClass } from '@angular/common';
import {
  Component,
  HostListener,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { PlatformSearchDialog } from 'components/platform-search-dialog/platform-search-dialog';
import { ADMIN_ROUTE_PATHS } from 'core/constants/admin-route-paths';
import { NavigationLayout } from 'core/models/platform-settings.model';
import { User } from 'core/models/user.model';
import { ROLE_LABELS } from 'core/models/role.model';
import { AuthSessionService } from 'core/services/auth-session.service';
import { FullscreenService } from 'core/services/fullscreen.service';
import { NotificationService } from 'core/services/notification.service';
import { PlatformSettingsService } from 'core/services/platform-settings.service';
import { ProfileService } from 'core/services/profile.service';
import { UserService } from 'core/services/user.service';
import { NotificationsDropdown } from 'ui/notifications-dropdown/notifications-dropdown';
import { UserAvatar } from 'ui/user-avatar/user-avatar';

@Component({
  selector: 'app-header',
  imports: [
    NgClass,
    RouterLink,
    NotificationsDropdown,
    PlatformSearchDialog,
    UserAvatar,
  ],
  templateUrl: './header.html',
})
export class Header {
  private readonly notifications = inject(NotificationService);
  private readonly fullscreen = inject(FullscreenService);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthSessionService);
  private readonly profileService = inject(ProfileService);
  private readonly userService = inject(UserService);
  private readonly platformSettings = inject(PlatformSettingsService);

  readonly title = input('SGE OneEduca');
  readonly userName = input('Administrador');
  readonly userRoleLabel = input('');
  readonly sidebarCollapsed = input(false);
  readonly navigationLayout = input<NavigationLayout>('sidebar');
  /** Offset from viewport top (e.g. trial banner height). */
  readonly topOffset = input('0px');
  readonly loginCandidates = input<readonly User[]>([]);
  readonly currentUserId = input('');
  readonly toggleSidebar = output<void>();
  readonly logout = output<void>();
  readonly switchUser = output<string>();

  protected readonly isUserMenuOpen = signal(false);
  protected readonly isSearchOpen = signal(false);
  protected readonly roleLabels = ROLE_LABELS;
  protected readonly notificationItems = this.notifications.recent;
  protected readonly notificationUnreadCount = this.notifications.unreadCount;
  protected readonly notificationsRoute = ADMIN_ROUTE_PATHS.notificationsList;
  protected readonly fullscreenSupported = this.fullscreen.isSupported;
  protected readonly isFullscreen = this.fullscreen.active;
  protected readonly searchRole = computed(() => this.auth.role());
  protected readonly searchGroupNavMode = computed(
    () => this.platformSettings.current().groupNavMode,
  );
  protected readonly searchShortcutLabel = this.resolveSearchShortcutLabel();
  /** Foto da sessão ou `avatar_url` do usuário. Vazio: iniciais. */
  protected readonly userAvatarUrl = computed(() => {
    const user = this.auth.user();
    if (!user) {
      return null;
    }
    const cached = this.userService.getById(user.id);
    return this.profileService.avatarUrl(
      user.id,
      cached?.avatarUrl ?? user.avatarUrl,
    );
  });

  protected handleOpenSearch(): void {
    this.handleCloseUserMenu();
    this.isSearchOpen.set(true);
  }

  protected handleCloseSearch(): void {
    this.isSearchOpen.set(false);
  }

  protected handleToggleFullscreen(): void {
    this.fullscreen.toggle();
  }

  protected handleDismissNotification(id: string): void {
    void this.notifications.confirmAndDismiss(id);
  }

  protected handleNavigateNotification(event: {
    id: string;
    actionUrl: string;
  }): void {
    void this.router.navigateByUrl(event.actionUrl);
    this.notifications.markRead(event.id);
  }

  protected handleToggleUserMenu(): void {
    this.isUserMenuOpen.update((open) => !open);
  }

  protected handleCloseUserMenu(): void {
    this.isUserMenuOpen.set(false);
  }

  protected onLogoutClick(event: Event): void {
    event.preventDefault();
    this.handleCloseUserMenu();
    this.logout.emit();
  }

  protected onSwitchUserChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const userId = select.value;
    if (userId) {
      this.switchUser.emit(userId);
      this.handleCloseUserMenu();
    }
  }

  @HostListener('document:keydown', ['$event'])
  protected handleSearchShortcut(event: KeyboardEvent): void {
    const key = event.key?.toLowerCase();
    if (!key || !(event.ctrlKey || event.metaKey) || key !== 'k') {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (
      target &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable)
    ) {
      return;
    }

    event.preventDefault();
    this.handleOpenSearch();
  }

  @HostListener('document:click', ['$event'])
  protected handleDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const menu = document.getElementById('header-user-menu');
    const toggle = document.getElementById('header-user-menu-toggle');

    if (
      this.isUserMenuOpen() &&
      menu &&
      toggle &&
      !menu.contains(target) &&
      !toggle.contains(target)
    ) {
      this.handleCloseUserMenu();
    }
  }

  private resolveSearchShortcutLabel(): string {
    if (typeof navigator === 'undefined') {
      return 'Ctrl K';
    }

    const platform = navigator.platform || '';
    const userAgent = navigator.userAgent || '';
    const isApple = /Mac|iPhone|iPad|iPod/i.test(`${platform} ${userAgent}`);
    return isApple ? '⌘ K' : 'Ctrl K';
  }
}
